import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Rocket, Download, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const CallToAction = () => {
  return (
    <section className="py-20 bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
              <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                JOIN THE REVOLUTION
              </span>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Ready to Transform Your{" "}
              <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                Restaurant?
              </span>
            </h2>
            <p className="text-white/80 text-base max-w-3xl mx-auto">
              Whether you need AI phone ordering or a smart menu assistant,
              Dine AI has the solution to grow your revenue.
            </p>
          </motion.div>

          {/* Two-column CTA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Restaurant Owners CTA */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#2256e5] rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-shadow hover:scale-105"
            >
              <div className="mb-6">
                <Rocket className="w-12 h-12 text-dineai-yellow mb-4" />
                <h3 className="text-2xl font-bold mb-2">For Restaurant Owners</h3>
                <p className="text-white/80">
                  From AI phone ordering to smart menu assistants - boost revenue by 15-30%
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-dineai-yellow/20 flex items-center justify-center">
                    <span className="text-dineai-yellow text-xs">✓</span>
                  </div>
                  <span className="text-white/90">AI Virtual Host - Never miss a call</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-dineai-yellow/20 flex items-center justify-center">
                    <span className="text-dineai-yellow text-xs">✓</span>
                  </div>
                  <span className="text-white/90">AI Virtual Assistant - Smart menu insights</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-dineai-yellow/20 flex items-center justify-center">
                    <span className="text-dineai-yellow text-xs">✓</span>
                  </div>
                  <span className="text-white/90">Ready in minutes, not months</span>
                </li>
              </ul>

              <div className="space-y-3">
                <Link to="/restaurant-menu-search">
                  <Button className="w-full bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all">
                    SIGN UP NOW
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <p className="text-white/60 text-sm text-center">
                  Simple pricing • No contracts
                </p>
              </div>
            </motion.div>

            {/* Customers CTA */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#2256e5] rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-shadow hover:scale-105"
            >
              <div className="mb-6">
                <Download className="w-12 h-12 text-dineai-yellow mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">For Food Lovers</h3>
                <p className="text-white/80">
                  Discover perfect meals tailored to your taste, dietary needs, and mood in seconds
                </p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-dineai-yellow/20 flex items-center justify-center">
                    <span className="text-dineai-yellow text-xs">✓</span>
                  </div>
                  <span className="text-white/90">AI-powered personalized recommendations</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-dineai-yellow/20 flex items-center justify-center">
                    <span className="text-dineai-yellow text-xs">✓</span>
                  </div>
                  <span className="text-white/90">Dietary restrictions & allergy filters</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-dineai-yellow/20 flex items-center justify-center">
                    <span className="text-dineai-yellow text-xs">✓</span>
                  </div>
                  <span className="text-white/90">Group dining coordination</span>
                </li>
              </ul>
              
              <div className="space-y-4">
                <div className="flex gap-4 justify-center">
                  <a href="https://apps.apple.com/us/app/dine-ai/id6743492809" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                    <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                      <img
                        src="/apple-store.png"
                        alt="Download on the App Store"
                        className="h-14 w-auto"
                      />
                    </div>
                  </a>

                  <a href="https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                    <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                      <img
                        src="/google-playstore.png"
                        alt="Get it on Google Play"
                        className="h-14 w-auto"
                      />
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Trust Section */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <p className="text-white/80 text-lg">
              Be among the <span className="font-bold text-dineai-yellow">first restaurants</span> and{" "}
              <span className="font-bold text-dineai-yellow">early adopters</span> to experience Dine AI
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;